import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet } from "react-native";
import { EffectiveFeeInput } from "../../../__generated__/graphql";
import Modal from "../../Modal";
import ScreenCard from "../../../screens/ScreenCard";
import SimpleHeader from "../../SimpleHeader";
import VStack from "../../../ui_atoms/VStack";
import { Controller, useForm } from "react-hook-form";
import TextField from "../../TextField";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import Text from "../../Text";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import { useValidateFieldMinMax } from "../../../hooks/use-validate-field-min-max";
import MoneyField from "../../MoneyField";

export type CreateEffectiveFeeFormData = {
    amount?: number;
    description?: string;
};

type CreateEffectiveFeeModalProps = {
    onCreate: (effectiveFee: EffectiveFeeInput) => void;
    onClose: () => void;
    visible: boolean;
    initialData?: CreateEffectiveFeeFormData;
};

const CreateEffectiveFeeModal: React.FC<CreateEffectiveFeeModalProps> = ({
    onClose,
    onCreate,
    visible,
    initialData,
}) => {
    const styles = useStyles();
    const { fonts } = useTheme();
    const i18n = useI18n();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isValid },
    } = useForm<CreateEffectiveFeeFormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            amount: undefined,
            description: "",
        },
    });
    useEffect(() => {
        if (visible) {
            if (initialData) {
                setValue("amount", (initialData.amount || 0) / 100);
                setValue("description", initialData.description);
            } else {
                reset();
            }
        }
    }, [visible, reset, initialData, setValue]);

    const handleCreateEffectiveFee = useCallback(
        (formData: CreateEffectiveFeeFormData) => {
            onCreate({
                amount: isNaN(formData.amount || 0) ? 0 : (formData.amount || 0) * 100,
                description: formData.description!,
            });
        },
        [onCreate],
    );

    const { chainInputFocus } = useChainInputFocus();
    const validateFieldMinMax = useValidateFieldMinMax();

    return (
        <Modal visible={visible} onClose={onClose} hideCloseButton>
            <SimpleHeader title={i18n.t("effective_fee.title")} onClose={onClose} mode="small" />
            <ScreenCard
                buttons={[
                    {
                        onPress: handleSubmit(handleCreateEffectiveFee),
                        mode: "secondary",
                        isValid: isValid,
                        title: !initialData ? i18n.t("add") : i18n.t("edit"),
                    },
                ]}
            >
                <VStack gap={1} h="2/3">
                    <Controller
                        name="amount"
                        control={control}
                        rules={{
                            required: true,

                            validate: {
                                minMax: (value) =>
                                    validateFieldMinMax({
                                        value,
                                        max: 1_000_000,
                                    }),
                            },
                        }}
                        render={({
                            field: { onChange, value, onBlur, name },
                            fieldState: { error },
                        }) => {
                            return (
                                <VStack gap="1/3">
                                    <Text style={styles.amountTitle}>
                                        {i18n.t("effective_fee.amount_title")}
                                    </Text>
                                    <MoneyField
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        error={error?.message}
                                        inputStyle={fonts.headlineMedium}
                                        showErrorText
                                        {...chainInputFocus(name)}
                                    />
                                </VStack>
                            );
                        }}
                    />
                    <Controller
                        name="description"
                        control={control}
                        rules={{ required: true }}
                        render={({
                            field: { onChange, value, onBlur, name },
                            fieldState: { error },
                        }) => (
                            <TextField
                                label={i18n.t(`effective_fee.field_description`)}
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                error={error?.message}
                                showErrorText
                                multiline
                                {...chainInputFocus(name)}
                            />
                        )}
                    />
                </VStack>
            </ScreenCard>
        </Modal>
    );
};

const useStyles = () => {
    const { fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                amountTitle: {
                    ...fonts.bodyMedium,
                },
            }),
        [fonts.bodyMedium],
    );
};

export default CreateEffectiveFeeModal;
